/**
 * Location Request Manager
 *
 * Centralizes all geolocation requests to prevent race conditions
 * and multiple simultaneous requests that can cause WebView crashes.
 */

export interface LocationCoords {
  latitude: number;
  longitude: number;
}

interface LocationRequestOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

interface PendingRequest {
  resolve: (value: LocationCoords) => void;
  reject: (error: Error) => void;
  options: LocationRequestOptions;
  startTime: number;
}

class LocationManager {
  private static instance: LocationManager;
  private pendingRequest: PendingRequest | null = null;
  private lastSuccessfulLocation: LocationCoords | null = null;
  private lastRequestTime: number = 0;
  private cooldownMs: number = 10000; // 10 seconds between requests
  private focusDelayMs: number = 3000; // 3 seconds delay after app focus

  private constructor() {
    // Setup app focus listener to add delay on app restart
    this.setupFocusListener();
  }

  static getInstance(): LocationManager {
    if (!LocationManager.instance) {
      LocationManager.instance = new LocationManager();
    }
    return LocationManager.instance;
  }

  /**
   * Setup listener for when app comes into focus
   * Adds a delay before allowing location requests after app restart
   */
  private setupFocusListener(): void {
    let lastFocusTime = 0;

    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusTime;

      // If it's been less than 30 seconds since last focus, treat as app resume
      // Add extra cooldown
      if (timeSinceLastFocus < 30000 && lastFocusTime > 0) {
        this.cooldownMs = 15000; // Increase cooldown after quick app switch
      } else {
        this.cooldownMs = 10000; // Normal cooldown
      }

      lastFocusTime = now;
    };

    window.addEventListener('focus', handleFocus);
  }

  /**
   * Get current location with rate limiting and request coalescing
   */
  async getLocation(options: LocationRequestOptions = {}): Promise<LocationCoords> {
    const {
      enableHighAccuracy = true,
      timeout = 10000,
      maximumAge = 300000 // 5 minutes
    } = options;

    const now = Date.now();

    // Check if we have a recent location we can use
    if (this.lastSuccessfulLocation && (now - this.lastRequestTime) < maximumAge) {
      return this.lastSuccessfulLocation;
    }

    // If there's already a pending request, wait for it instead of making a new one
    if (this.pendingRequest) {
      return new Promise((resolve, reject) => {
        // Clone the pending request's promise
        this.pendingRequest!.resolve = resolve;
        this.pendingRequest!.reject = reject;
      });
    }

    // Check cooldown period
    if (now - this.lastRequestTime < this.cooldownMs) {
      const waitTime = this.cooldownMs - (now - this.lastRequestTime);
      await this.delay(waitTime);
    }

    // Check if geolocation is available
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    }

    // Create the promise for this request
    return new Promise((resolve, reject) => {
      this.pendingRequest = {
        resolve: () => {}, // Will be replaced
        reject: () => {},  // Will be replaced
        options,
        startTime: now
      };

      // Set up the actual resolve/reject
      const requestPromise = new Promise<LocationCoords>((res, rej) => {
        this.pendingRequest!.resolve = res;
        this.pendingRequest!.reject = rej;
      });

      // Make the geolocation request
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: LocationCoords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };

          // Cache the successful location
          this.lastSuccessfulLocation = coords;
          this.lastRequestTime = Date.now();

          // Resolve all pending requests
          if (this.pendingRequest) {
            this.pendingRequest.resolve(coords);
            this.pendingRequest = null;
          }

          resolve(coords);
        },
        (error) => {
          let errorMessage: string;

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable it in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable. Please try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'Failed to get location. Please try again.';
          }

          const locationError = new Error(errorMessage);

          // Reject all pending requests
          if (this.pendingRequest) {
            this.pendingRequest.reject(locationError);
            this.pendingRequest = null;
          }

          reject(locationError);
        },
        {
          enableHighAccuracy,
          timeout,
          maximumAge
        }
      );
    });
  }

  /**
   * Simple delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear cached location (force fresh request next time)
   */
  clearCache(): void {
    this.lastSuccessfulLocation = null;
    this.lastRequestTime = 0;
  }

  /**
   * Get cached location if available
   */
  getCachedLocation(): LocationCoords | null {
    return this.lastSuccessfulLocation;
  }
}

export const locationManager = LocationManager.getInstance();
